import clsx from 'clsx'

import { Flex } from '@buffer-mono/popcorn'

import { VideoPlayer } from '~publish/legacy/composer/composer/components/post-preview/previews/Common/VideoPlayer'
import type {
  Gif,
  Link,
} from '~publish/legacy/composer/composer/entities/factories'
import { AttachmentTypes } from '~publish/legacy/constants'
import { LinkifiedText } from '~publish/legacy/shared-components/LinkifiedText'
import { parseBlueskyLinks } from '~publish/legacy/utils/channels/bluesky/helpers'

import {
  CommentIcon,
  DotsIcon,
  HeartIcon,
  RepostIcon,
  SaveIcon,
  ShareIcon,
} from '../../../../assets/bluesky'
import useLinkPreviewEffect from '../../../Common/hooks/useLinkPreviewEffect'
import { ImagesGrid } from '../../../Common/ImagesGrid'
import { LinkPreview } from '../../../Common/LinkPreview'
import { ThreadsConnector } from '../../../Common/styles'
import { gifAsImage, truncateBlueskyText } from '../../../Common/utils'
import type { ProfilePreview, ThreadsContent } from '../../../types'

import styles from './BlueskyPost.module.css'

import type { JSX } from 'react'

interface GetMediaPropertiesResult {
  hasImages: boolean | number | undefined | null | Gif
  hasLink: boolean
  hasVideo: boolean
  videoRatio: number
}
const MIN_VIDEO_RATIO = 9 / 22
const MAX_VIDEO_RATIO = 4 / 1

const getMediaProperties = (
  content: ThreadsContent,
  linkPreview: Link | null,
): GetMediaPropertiesResult => {
  const { images, gif, enabledAttachmentType, video } = content

  const hasMedia = enabledAttachmentType === AttachmentTypes.MEDIA
  const hasImages = hasMedia && (images?.length || gif)
  const hasVideo = hasMedia && !!video
  const hasLink = !!(linkPreview && !hasImages && !hasVideo)
  const videoRatio = hasVideo ? video.width / video.height : 0

  return { hasImages, hasLink, hasVideo, videoRatio }
}

export const BlueskyPost = ({
  content,
  profile,
  index,
}: {
  content: ThreadsContent
  profile: ProfilePreview
  index: number
}): JSX.Element => {
  const {
    text,
    images,
    gif,
    link: linkFromPost,
    enabledAttachmentType,
    video,
  } = content
  const { avatar, username, displayName, formattedUsername } = profile
  const [linkPreview] = useLinkPreviewEffect({
    linkFromPost,
    text,
    useScraperData: false,
  })

  // Bluesky link cards are explicit embeds: a card is only published while the
  // link attachment is enabled, never derived from URLs left in the text
  const effectiveLinkPreview =
    enabledAttachmentType === AttachmentTypes.LINK ? linkPreview : null

  const { hasImages, hasLink, hasVideo, videoRatio } = getMediaProperties(
    content,
    effectiveLinkPreview,
  )
  const allImages = images?.length ? images : gif ? [gifAsImage(gif)] : []
  const usernameToDisplay = displayName || username

  const cutToSquare =
    hasVideo && (videoRatio > MAX_VIDEO_RATIO || videoRatio < MIN_VIDEO_RATIO)

  return (
    <div className={styles.wrapper} data-thread-id={index}>
      <Flex
        direction="column"
        align="center"
        justify="start"
        className={styles.avatarWrapper}
      >
        <img
          className={styles.avatar}
          data-testid="avatar"
          src={avatar}
          alt="user avatar"
        />
        <ThreadsConnector />
      </Flex>
      <div className={clsx(styles.content, cutToSquare && styles.cutToSquare)}>
        <div className={styles.header}>
          <div className={styles.username} translate="no">
            {usernameToDisplay}
          </div>
          <div className={styles.formattedUserName} translate="no">
            {formattedUsername}
          </div>
          <div className={styles.ago}>· 21h</div>
        </div>

        {text ? (
          <LinkifiedText
            links={parseBlueskyLinks(text)}
            whitespace="pre-wrap"
            className={styles.text}
          >
            {truncateBlueskyText(text, 300)}
          </LinkifiedText>
        ) : (
          <span />
        )}

        {hasImages && (
          <ImagesGrid
            className={styles.imageGrid}
            images={allImages}
            isRowFormat={false}
            maxHeight="180px"
            shouldShowGifTag={false}
            roundedCorners
          />
        )}

        {hasVideo && video && <VideoPlayer video={video} />}

        {hasLink && effectiveLinkPreview && (
          <LinkPreview
            className={styles.linkPreview}
            link={effectiveLinkPreview}
            clickable={false}
            imageCover
            showDescription
          />
        )}

        <Flex justify="between" className={styles.footer}>
          <Flex align="center" gap="2xl">
            <CommentIcon size={16} className={styles.icon} />
            <RepostIcon size={16} className={styles.icon} />
            <HeartIcon size={16} className={styles.icon} />
          </Flex>
          <Flex align="center" gap="sm">
            <SaveIcon size={16} className={styles.icon} />
            <ShareIcon size={16} className={styles.icon} />
            <DotsIcon size={16} className={styles.icon} />
          </Flex>
        </Flex>
      </div>
    </div>
  )
}
